import { AlertTriangle } from 'lucide-react';

import { PolicyReviewResponse } from '@/types/service/policyUserReview';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DeleteReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  review: PolicyReviewResponse | null;
  onConfirm: (reviewId: number) => Promise<void>;
  isDeleting: boolean;
}

const DeleteReviewDialog = ({
  open,
  onOpenChange,
  review,
  onConfirm,
  isDeleting,
}: DeleteReviewDialogProps) => {
  if (!review) return null;

  const handleConfirm = async () => {
    try {
      await onConfirm(review.id);
      onOpenChange(false);
    } catch (error) {
      // Error handled by parent component
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-destructive' />
            <AlertDialogTitle>Delete Policy Review</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            Are you sure you want to delete this policy review? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className='bg-muted p-4 rounded-lg space-y-2 text-sm'>
          <div>
            <span className='font-medium'>User:</span>{' '}
            <span>
              {review.user_first_name} {review.user_last_name} ({review.user_email})
            </span>
          </div>
          <div>
            <span className='font-medium'>Policy:</span> <span>{review.policy_name}</span>
          </div>
          {review.group_name && (
            <div>
              <span className='font-medium'>Group:</span> <span>{review.group_name}</span>
            </div>
          )}
          <div>
            <span className='font-medium'>Status:</span>{' '}
            <span>{review.approved ? 'Approved' : 'Pending'}</span>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className='bg-destructive hover:bg-destructive/90'
          >
            {isDeleting ? 'Deleting...' : 'Delete Review'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteReviewDialog;
